import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentsApi } from '../../../api/client';
import { Button, Badge } from '../../ui/Modal';
import AgentEditModal from './AgentEditModal';
import CsvImportWizard from './CsvImportWizard';
import HelpButton from '../../help/HelpButton';

export default function AgentsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [enabledFilter, setEnabledFilter] = useState('all'); // all | enabled | disabled
  const [queueFilter, setQueueFilter] = useState('');
  const [editing, setEditing] = useState(null);   // null | 'new' | { ...agent }
  const [importing, setImporting] = useState(false);

  const params = useMemo(() => ({
    search: search || undefined,
    queue: queueFilter || undefined,
    enabled: enabledFilter === 'enabled' ? true : enabledFilter === 'disabled' ? false : undefined,
  }), [search, queueFilter, enabledFilter]);

  const { data: agents = [], isLoading, refetch } = useQuery({
    queryKey: ['agents', params],
    queryFn: () => agentsApi.list(params),
  });

  const deleteMut = useMutation({
    mutationFn: ({ id, hard }) => agentsApi.remove(id, { hard }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  });

  const syncMut = useMutation({
    mutationFn: (id) => agentsApi.sync(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  });

  const enableMut = useMutation({
    // Re-enable via PATCH enabled=true; the existing endpoint handles status flip.
    mutationFn: (id) => agentsApi.update(id, { enabled: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  });

  const allQueues = useMemo(() => {
    const set = new Set();
    agents.forEach((a) => (a.queues || []).forEach((q) => set.add(q)));
    return Array.from(set).sort();
  }, [agents]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">Agents</h1>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted">
            Provision contact-center agents. SIP credentials are generated server-side and pushed to Kamailio automatically.
          </p>
        </div>
        <div className="flex gap-2">
          <HelpButton moduleKey="admin_agents" />
          <Button onClick={() => setImporting(true)}>📥 Import CSV</Button>
          <Button variant="primary" onClick={() => setEditing('new')}>+ New agent</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search extension, name, email, WIN ID…"
          className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base text-sm flex-1 min-w-[240px]"
        />
        <select
          value={enabledFilter}
          onChange={(e) => setEnabledFilter(e.target.value)}
          className="px-2 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base text-sm"
        >
          <option value="all">All</option>
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
        </select>
        <select
          value={queueFilter}
          onChange={(e) => setQueueFilter(e.target.value)}
          className="px-2 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base text-sm"
        >
          <option value="">All queues</option>
          {allQueues.map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
        <Button variant="ghost" onClick={() => refetch()}>↻ Refresh</Button>
      </div>

      <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle dark:bg-surface-dark-subtle text-left">
            <tr className="text-xs uppercase tracking-wider text-surface-muted dark:text-surface-dark-muted">
              <th className="px-3 py-2">Ext</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Queues</th>
              <th className="px-3 py-2">WIN ID</th>
              <th className="px-3 py-2">Skill</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">SIP Sync</th>
              <th className="px-3 py-2">MFA</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-surface-muted">Loading…</td></tr>
            )}
            {!isLoading && agents.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-10 text-center text-surface-muted">
                  No agents yet. Click <strong>+ New agent</strong> or <strong>📥 Import CSV</strong> to start.
                </td>
              </tr>
            )}
            {agents.map((a) => (
              <tr key={a.id} className="border-t border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle/40 dark:hover:bg-surface-dark-subtle/40">
                <td className="px-3 py-2 font-mono">{a.extension}</td>
                <td className="px-3 py-2">{a.name}</td>
                <td className="px-3 py-2 text-surface-muted">{a.email}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(a.queues || []).map((q) => <Badge key={q} tone="blue">{q}</Badge>)}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">{a.winid || '—'}</td>
                <td className="px-3 py-2 text-center">{a.skill_level}</td>
                <td className="px-3 py-2">
                  {a.status === 'active'   ? <Badge tone="green">Active</Badge> :
                   a.status === 'invited'  ? <Badge tone="amber">Invited</Badge> :
                   a.status === 'disabled' ? <Badge tone="red">Disabled</Badge> :
                                              <Badge>{a.status}</Badge>}
                </td>
                <td className="px-3 py-2">
                  {a.sip_synced ? (
                    <Badge tone="green">✓ synced</Badge>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Badge tone="amber">unsynced</Badge>
                      <button
                        onClick={() => syncMut.mutate(a.id)}
                        disabled={syncMut.isPending}
                        className="text-xs text-continuum-700 dark:text-continuum-300 hover:underline disabled:opacity-50"
                        title="Push this agent's subscriber row to Kamailio"
                      >
                        {syncMut.isPending ? 'syncing…' : 'Re-sync'}
                      </button>
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {a.mfa_enrolled ? <Badge tone="green">✓</Badge> : <Badge tone="amber">—</Badge>}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" onClick={() => setEditing(a)}>Edit</Button>
                    {a.status === 'disabled' ? (
                      <Button
                        variant="primary"
                        onClick={() => {
                          if (confirm(`Re-enable agent ${a.extension} (${a.email})?\n\nKamailio will accept their REGISTER again.`)) {
                            enableMut.mutate(a.id);
                          }
                        }}
                      >Enable</Button>
                    ) : (
                      <Button
                        variant="danger"
                        onClick={() => {
                          if (confirm(`Disable agent ${a.extension} (${a.email})?\n\nThis will deny their next REGISTER. Their account history is preserved.`)) {
                            deleteMut.mutate({ id: a.id, hard: false });
                          }
                        }}
                      >Disable</Button>
                    )}
                    <Button
                      variant="danger"
                      title="Remove the agent assignment from this user. The user account stays so they can still log in."
                      onClick={() => {
                        const first = confirm(
                          `⚠️ REMOVE agent assignment from ${a.email} (extension ${a.extension})?\n\n` +
                          `This will:\n` +
                          `  • Remove the extension and SIP credentials\n` +
                          `  • Remove queue memberships and dialing profile\n` +
                          `  • Remove caller-ID from both Issabel AstDBs\n` +
                          `  • Remove the Kamailio subscriber row (they can't REGISTER)\n\n` +
                          `The USER ACCOUNT stays — they can still log in as admin/supervisor.\n` +
                          `To delete the user entirely, use Settings → Users.\n\n` +
                          `Prefer Disable for normal off-boarding (preserves caller-ID + queues).`
                        );
                        if (!first) return;
                        const typed = prompt(
                          `Type the extension (${a.extension}) to confirm removal:`
                        );
                        if (typed !== String(a.extension)) {
                          if (typed !== null) alert('Extension did not match. Cancelled.');
                          return;
                        }
                        deleteMut.mutate({ id: a.id, hard: true });
                      }}
                    >Remove agent</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-surface-muted dark:text-surface-dark-muted">
        {agents.length} agent{agents.length === 1 ? '' : 's'} shown.
      </div>

      {editing && (
        <AgentEditModal
          agent={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['agents'] }); }}
        />
      )}
      {importing && (
        <CsvImportWizard
          onClose={() => setImporting(false)}
          onDone={() => { setImporting(false); qc.invalidateQueries({ queryKey: ['agents'] }); }}
        />
      )}
    </div>
  );
}
